import { useCallback, useEffect, useState } from "react";
import {
  ShieldCheck, Check, Plus, Star, MessageCircle, Camera, X, ChevronRight, BadgeCheck, Lock,
} from "lucide-react";

import { api } from "../lib/api";
import { date, today } from "../lib/fmt";
import { Sheet, Glass, Skeleton, Err, Pill } from "../ui";
import { Mark } from "../brand/Mark";

/**
 * CareSheet — the two-year cover, as a client sees it.
 *
 * Behaviour is the V7 Care tab; the shell and the material are new. Two
 * corrections against that version, both found by reading the route rather
 * than the component:
 *
 *  · The review block read `card.review`. The server sends `steps.review`, so
 *    the block never rendered and no client has ever been shown the review
 *    prompt from inside the app. It reads `card.steps.review` here.
 *  · A schedule entry has no `label`; it has `month`. "Visit 1" was being
 *    printed where "6-month service" was meant.
 *
 * The charge gate is unchanged and deliberately conservative: the fee is
 * quoted the moment a date is picked, not on submit, and Send stays disabled
 * until the client ticks the box. The server enforces the same rule (409
 * needs_fee_ack), so the worst case is an ugly error rather than an unbilled
 * visit.
 */

const ISSUE_TYPES = [
  ["carpentry", "Carpentry"], ["hardware", "Hardware"], ["finish", "Finish / laminate"],
  ["electrical", "Electrical"], ["plumbing", "Plumbing"], ["false-ceiling", "False ceiling"],
  ["furniture", "Furniture"], ["appliance", "Appliance"], ["other", "Something else"],
];
const SLOTS = [["morning", "Morning"], ["afternoon", "Afternoon"], ["evening", "Evening"], ["any", "Any time"]];

export function CareSheet({ ctx, onClose }) {
  const care = useCare();
  return (
    <Sheet title="D'leaps Care" onClose={onClose}>
      <CareBody care={care} />
    </Sheet>
  );
}

/** Shared with the drawer badge, which needs the card without the sheet. */
export function useCare() {
  const [card, setCard] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, r] = await Promise.all([
        api.get("/portal/care"),
        api.get("/portal/care/requests").catch(() => ({ requests: [] })),
      ]);
      setCard(c);
      setRequests(r.requests || []);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  return { card, requests, loading, err, setErr, load };
}

function CareBody({ care }) {
  const { card, requests, loading, err, setErr, load } = care;
  const [form, setForm] = useState(false);

  if (loading) return <Skeleton h={180} />;
  if (!card) return <Err>{err || "Care is not available on this project."}</Err>;

  const state = card.needs_end_date ? "pending"
    : card.expired ? "expired"
      : card.not_started ? "soon"
        : card.active ? "active" : "pending";

  const review = card.steps?.review;
  const payment = card.steps?.payment;

  async function confirmReview(undo) {
    try {
      await api.post("/portal/care", { action: undo ? "undo-review" : "confirm-review" });
      load();
    } catch (e) { setErr(e.message); }
  }

  return (
    <>
      <Err onDismiss={() => setErr("")}>{err}</Err>

      {/* ---- the card ------------------------------------------------------
           A flat printed object, not glass. It is the closest thing the client
           has to a warranty card and it should read like one — same gradient,
           same watermark, same three fields as the card they already know. */}
      <section className={"carecard" + (state === "active" ? "" : " locked")}>
        <span className="wm" aria-hidden="true"><Mark size={158} plain /></span>

        <div className="kicker">D&rsquo;leaps Care</div>

        {state === "active" ? (
          <>
            <div className="big">{card.client_name || "D'leaps client"}</div>
            <div className="yrs">
              {card.months === 24 ? "2 years" : `${card.months || 24} months`} of free service
              {card.project ? ` · ${card.project}` : ""}
            </div>
            <div className="grid">
              <div className="f">
                <div className="fl">Card no.</div>
                <div className="fv no">{card.card_no}</div>
              </div>
              <div className="f">
                <div className="fl">Covered from</div>
                <div className="fv">{date(card.starts)}</div>
              </div>
              <div className="f">
                <div className="fl">Covered until</div>
                <div className="fv">{date(card.expires)}</div>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="big">
              {card.months === 24 ? "2 years free" : `${card.months || 24} months free`}
            </div>
            <div className="yrs">{lockedLine(card, state)}</div>
            <div className="grid">
              <div className="f">
                <div className="fl">Status</div>
                <div className="fv">{lockedStatus(state)}</div>
              </div>
              <div className="f">
                <div className="fl">Project</div>
                <div className="fv">{card.project || "—"}</div>
              </div>
              {state === "soon" && card.starts && (
                <div className="f">
                  <div className="fl">Begins</div>
                  <div className="fv">{date(card.starts)}</div>
                </div>
              )}
            </div>
            <span className="lk"><Lock size={20} /></span>
          </>
        )}
      </section>

      {/* ---- book the next visit ------------------------------------------
           WhatsApp, with the card number and the visit already in the message.
           A client should not have to explain which service they mean. */}
      {state === "active" && card.next_service && card.whatsapp && (
        <a
          className="care-book"
          href={
            card.whatsapp + "?text=" +
            encodeURIComponent(
              `Hello D'leaps — I would like to book my ${card.next_service.month}-month ` +
              `D'leaps Care service. Card ${card.card_no}.`
            )
          }
          target="_blank"
          rel="noopener noreferrer"
        >
          <MessageCircle size={16} /> Book your {card.next_service.month}-month service
        </a>
      )}

      {/* ---- what activates it --------------------------------------------- */}
      {state !== "active" && (payment || review) && (
        <Glass tint style={{ padding: 16, marginBottom: 14 }}>
          <p className="sec-title" style={{ padding: 0, marginTop: 0 }}>To activate your cover</p>
          {payment && <Step done={payment.done} label={payment.label} />}
          {review && <Step done={review.done} label={review.label} />}
        </Glass>
      )}

      {/* ---- service schedule --------------------------------------------- */}
      {Array.isArray(card.schedule) && card.schedule.length > 0 && (
        <>
          <h3 className="care-h">Your service visits</h3>
          {card.schedule.map((v, i) => (
            <div className={"vis " + (v.done ? "done" : v.status || "")} key={i}>
              <span className="d" aria-hidden="true">
                <b>{v.month}</b><span>mo</span>
              </span>
              <span className="vt">
                <span className="vh">{v.month}-month service</span>
                <span className="vs">
                  {v.done
                    ? `Done ${date(v.done_on || v.due)}`
                    : v.status === "passed"
                      ? `Was due ${date(v.due)}`
                      : `Due ${date(v.due)}`}
                </span>
              </span>
              {v.status === "due" && !v.done && <Pill tone="warn">Due soon</Pill>}
            </div>
          ))}
        </>
      )}

      {/* ---- service requests ---------------------------------------------- */}
      <div className="sec-head" style={{ padding: "16px 0 8px" }}>
        <h2 className="sec-title">Service requests</h2>
        <button className="sec-more" onClick={() => setForm(true)}>
          <Plus size={13} /> Raise a request
        </button>
      </div>

      {requests.length === 0 ? (
        <p className="muted" style={{ fontSize: ".86rem" }}>
          Nothing raised yet. If something needs attention, tell us here.
        </p>
      ) : (
        requests.map((r) => (
          <Glass className="row" key={r.id}>
            <span className="row-ic"><ShieldCheck size={16} /></span>
            <span className="row-txt">
              <b>{r.request_no || `#${r.id}`} · {r.room || "Unspecified room"}</b>
              <small>{r.description}</small>
              <small>
                Preferred {date(r.preferred_date)}
                {r.in_cover ? " · covered" : r.visit_fee ? ` · ₹${r.visit_fee} visit fee` : ""}
                {r.scheduled_for ? ` · scheduled ${date(r.scheduled_for)}` : ""}
              </small>
            </span>
            <Pill tone={r.status === "closed" ? "ok" : r.status === "open" ? "warn" : undefined}>
              {r.status || "open"}
            </Pill>
          </Glass>
        ))
      )}

      {/* ---- review -------------------------------------------------------- */}
      {review && (
        <Glass tint style={{ padding: 16, marginTop: 16, display: "flex", gap: 12, alignItems: "center" }}>
          <Star size={20} color="var(--mag-btn)" />
          <span style={{ flex: 1 }}>
            <b style={{ display: "block", fontSize: ".9rem" }}>{review.label}</b>
            {review.confirmed_by && <small className="muted">Confirmed by {review.confirmed_by}</small>}
          </span>
          {review.verified ? (
            <BadgeCheck size={20} color="var(--ok)" />
          ) : review.url && !review.confirmed ? (
            <a
              className="btn"
              href={review.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setTimeout(() => confirmReview(false), 1200)}
            >
              Open Google <ChevronRight size={14} />
            </a>
          ) : null}
        </Glass>
      )}

      {card.whatsapp && (
        <a className="btn block" style={{ marginTop: 14 }} href={card.whatsapp} target="_blank" rel="noopener noreferrer">
          <MessageCircle size={16} /> Message the studio on WhatsApp
        </a>
      )}

      {form && (
        <RequestForm
          close={() => setForm(false)}
          done={() => { setForm(false); load(); }}
        />
      )}
    </>
  );
}

function Step({ done, label }) {
  return (
    <div style={{ display: "flex", gap: 10, alignItems: "center", padding: "7px 0" }}>
      <span
        style={{
          width: 20, height: 20, borderRadius: "50%", display: "grid", placeItems: "center",
          background: done ? "var(--ok)" : "transparent",
          boxShadow: done ? "none" : "inset 0 0 0 1.5px var(--rule)",
          color: "#fff", flex: "0 0 auto",
        }}
      >
        {done && <Check size={12} />}
      </span>
      <span style={{ fontSize: ".88rem", color: done ? "var(--ink-soft)" : "var(--ink)" }}>{label}</span>
    </div>
  );
}

/** Why the card is not live yet, in the client's terms rather than the API's. */
function lockedLine(card, state) {
  if (state === "expired") return "Your cover has ended. Visits are chargeable from here.";
  if (state === "soon") return "Your cover starts the day we hand over.";
  return "Two more steps and this card is yours.";
}

function lockedStatus(state) {
  if (state === "expired") return "Ended";
  if (state === "soon") return "Starts at handover";
  return "Not yet active";
}

function coverPct(card) {
  const total = Number(card.months || 24) * 30;
  const left = Number(card.days_left || 0);
  if (!total) return 0;
  return Math.max(0, Math.min(100, Math.round((left / total) * 100)));
}

/* =========================================================================
   REQUEST FORM
   ========================================================================= */

function RequestForm({ close, done }) {
  const [room, setRoom] = useState("");
  const [type, setType] = useState("other");
  const [desc, setDesc] = useState("");
  const [when, setWhen] = useState(today());
  const [slot, setSlot] = useState("any");
  const [photos, setPhotos] = useState([]);
  const [quote, setQuote] = useState(null);
  const [accept, setAccept] = useState(false);
  const [busy, setBusy] = useState(false);
  const [local, setLocal] = useState("");

  useEffect(() => {
    let live = true;
    api.get(`/portal/care/quote?date=${when}`)
      .then((q) => { if (live) { setQuote(q); setAccept(false); } })
      .catch(() => { if (live) setQuote(null); });
    return () => { live = false; };
  }, [when]);

  async function upload(files) {
    for (const f of Array.from(files).slice(0, 8 - photos.length)) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const r = await api.upload("/portal/care/photo", fd);
        if (r.url) setPhotos((p) => [...p, { url: r.url, name: r.name }]);
      } catch (e) { setLocal(e.message); }
    }
  }

  async function submit() {
    setLocal("");
    if (!desc.trim()) return setLocal("Please tell us what needs attention.");
    setBusy(true);
    try {
      await api.post("/portal/care/requests", {
        room, issue_type: type, description: desc.trim(),
        preferred_date: when, slot, photos: photos.map((p) => p.url),
        accept_fee: accept ? 1 : 0,
      });
      done();
    } catch (e) {
      if (/fee_acknowledgement_required|needs_fee_ack/i.test(e.message)) {
        setLocal("Please tick the box confirming the visit fee, then send again.");
      } else {
        setLocal(e.message);
      }
      setBusy(false);
    }
  }

  const chargeable = quote && !quote.in_cover;

  return (
    <Sheet
      title="Raise a service request"
      onClose={close}
      className="veeha"
      footer={
        <div className="vh-ask">
          <button className="btn" style={{ flex: 1 }} onClick={close}>Cancel</button>
          <button
            className="btn primary"
            style={{ flex: 1 }}
            onClick={submit}
            disabled={busy || (chargeable && !accept)}
          >
            {busy ? "Sending…" : "Send request"}
          </button>
        </div>
      }
    >
      <Err onDismiss={() => setLocal("")}>{local}</Err>

      <div className="field">
        <label htmlFor="care-room">Room</label>
        <input id="care-room" value={room} maxLength={80} placeholder="Master bedroom" onChange={(e) => setRoom(e.target.value)} />
      </div>

      <div className="field">
        <label htmlFor="care-type">What kind of issue</label>
        <select id="care-type" value={type} onChange={(e) => setType(e.target.value)}>
          {ISSUE_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>

      <div className="field">
        <label htmlFor="care-desc">What needs attention</label>
        <textarea
          id="care-desc"
          rows={3}
          value={desc}
          maxLength={4000}
          placeholder="The wardrobe shutter is catching on the frame."
          onChange={(e) => setDesc(e.target.value)}
        />
      </div>

      <div className="grid2">
        <div className="field">
          <label htmlFor="care-date">Preferred date</label>
          <input id="care-date" type="date" value={when} min={today()} onChange={(e) => setWhen(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="care-slot">Time</label>
          <select id="care-slot" value={slot} onChange={(e) => setSlot(e.target.value)}>
            {SLOTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
      </div>

      <div className="field">
        <label>Photos ({photos.length}/8)</label>
        <label className="btn" style={{ justifySelf: "start" }}>
          <Camera size={15} /> Add photos
          <input
            type="file"
            accept="image/*"
            multiple
            hidden
            onChange={(e) => { upload(e.target.files); e.target.value = ""; }}
          />
        </label>
      </div>

      {photos.length > 0 && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
          {photos.map((p, i) => (
            <span key={i} style={{ position: "relative" }}>
              <img src={p.url} alt="" style={{ width: 64, height: 64, objectFit: "cover", borderRadius: 12 }} />
              <button
                onClick={() => setPhotos((x) => x.filter((_, n) => n !== i))}
                aria-label="Remove photo"
                style={{
                  position: "absolute", top: -6, right: -6, width: 20, height: 20, borderRadius: "50%",
                  background: "var(--mag-btn)", color: "#fff", display: "grid", placeItems: "center",
                }}
              >
                <X size={11} />
              </button>
            </span>
          ))}
        </div>
      )}

      {quote && (
        <Glass tint={chargeable} style={{ padding: 14 }}>
          {chargeable ? (
            <>
              <b style={{ display: "block", color: "var(--mag-btn)" }}>This visit is outside your cover.</b>
              <p className="muted" style={{ margin: "4px 0 10px", fontSize: ".84rem" }}>
                {quote.message || quote.reason}
              </p>
              <label style={{ display: "flex", gap: 9, alignItems: "flex-start", fontSize: ".85rem" }}>
                <input type="checkbox" checked={accept} onChange={(e) => setAccept(e.target.checked)} />
                <span>I understand a visit fee of {quote.fee_display} applies.</span>
              </label>
            </>
          ) : (
            <span style={{ display: "flex", gap: 8, alignItems: "center", fontSize: ".88rem" }}>
              <Check size={16} color="var(--ok)" /> <b>Covered.</b> No visit fee for this date.
            </span>
          )}
        </Glass>
      )}
    </Sheet>
  );
}
